import { useCallback, useMemo } from "react";

import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";

import { UsersIcon } from "lucide-react";

import { InvitationOfType, UseInvitationOptions } from "simplydo/interfaces";

import api from "api";

import {
  acceptAuthenticatedInvitation,
  rejectAuthenticatedInvitation,
  removeAuthenticatedInvitation,
  toastError,
  toastMessage,
  toastSuccess,
  withInvitationPrompt,
} from "./internal";
import type { InvitationViewModel } from "./types";
import { emptyInvitationViewModel } from "./types";

export const useGroupInvitation = (
  invitation: InvitationOfType<"group"> | undefined,
  options?: UseInvitationOptions,
): InvitationViewModel => {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const group = invitation?.group;
  const groupId = group?._id;
  const groupName = group?.name || t("invitations.deletedGroup");
  const inviteeName =
    invitation?.inviteeUser?.profile?.fullName || invitation?.invitee;
  const canAccept = !!invitation?.canAccept && !!group;
  const createdByCurrentUser = !!invitation?.createdByCurrentUser;

  const afterGroupAccept = useCallback(() => {
    if (!groupId) {
      return;
    }
    api.invalidateQueries(api.users.me).then(() => {
      api.invalidateQueries(api.groups.get, [groupId]);
      api.invalidateQueries(api.pages.getAll, ["group", groupId]);
      api.invalidateQueries(api.discussions.getAll, ["group", groupId]);
      navigate(`/groups/${groupId}`);
    });
  }, [groupId, navigate]);

  const acceptInvite = useCallback(() => {
    if (!invitation) {
      return;
    }
    withInvitationPrompt(
      options,
      t("invitations.group.acceptTitle"),
      t("invitations.group.acceptMessage", { name: group?.name }),
      () => {
        acceptAuthenticatedInvitation(
          invitation,
          () => {
            toastSuccess(t("invitations.group.accepted"));
            afterGroupAccept();
          },
          () => toastError(t("invitations.group.acceptError")),
        );
      },
    );
  }, [afterGroupAccept, group?.name, invitation, options, t]);

  const rejectInvite = useCallback(() => {
    if (!invitation) {
      return;
    }
    withInvitationPrompt(
      options,
      t("invitations.group.rejectTitle"),
      t("invitations.group.rejectMessage", { name: groupName }),
      () => {
        rejectAuthenticatedInvitation(
          invitation,
          () => toastMessage(t("invitations.rejected")),
          () => toastError(t("invitations.rejectError")),
        );
      },
    );
  }, [groupName, invitation, options, t]);

  const removeInvite = useCallback(() => {
    if (!invitation) {
      return;
    }
    withInvitationPrompt(
      options,
      t("invitations.group.removeTitle"),
      t("invitations.group.removeMessage", { name: groupName }),
      () => {
        removeAuthenticatedInvitation(
          invitation,
          () => toastMessage(t("invitations.removed")),
          () => toastError(t("invitations.removeError")),
        );
      },
    );
  }, [groupName, invitation, options, t]);

  return useMemo(() => {
    if (!invitation) {
      return emptyInvitationViewModel();
    }

    return {
      icon: UsersIcon,
      link: groupId ? `/groups/${groupId}` : null,
      isInvalid: !group,
      text: {
        title: groupName,
        fullTitle: t("invitations.group.fullPageTitle", { name: groupName }),
        youInvited: t("invitations.group.creatorPostText", { inviteeName }),
        hasInvitedYou: t("invitations.group.receivedPostText"),
        dismiss: createdByCurrentUser
          ? t("invitations.remove")
          : t("invitations.reject"),
        invalid: t("invitations.group.invalidMessage"),
        ...(groupId ? { goTo: t("invitations.group.view") } : {}),
      },
      acceptInvite: canAccept ? acceptInvite : undefined,
      rejectInvite: !createdByCurrentUser ? rejectInvite : undefined,
      removeInvite: createdByCurrentUser ? removeInvite : undefined,
    };
  }, [
    acceptInvite,
    canAccept,
    createdByCurrentUser,
    group,
    groupId,
    groupName,
    invitation,
    inviteeName,
    removeInvite,
    rejectInvite,
    t,
  ]);
};
